import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import { Card, CardContent } from '../components/ui/card';
import QuickReviewForm from '../components/QuickReviewForm';
import { 
    MapPin, 
    Loader2, 
    ArrowLeft,
    ArrowRight,
    Send,
    Star,
    Calendar,
    Camera,
    FileText,
    X,
    ShieldCheck,
    Briefcase,
    Mail,
    AlertTriangle,
    Zap,
    ClipboardList
} from 'lucide-react';
import { addressAPI, reviewAPI, authAPI } from '../lib/api';
import { JOB_TYPES, getErrorMessage } from '../lib/utils';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

// Star Rating Component
const StarRating = ({ value, onChange }) => {
    const [hovered, setHovered] = useState(0);
    const labels = {
        1: 'Major Issues',
        2: 'Difficult',
        3: 'Okay',
        4: 'Good',
        5: 'Great'
    };
    
    return (
        <div className="flex flex-col items-center gap-3">
            <div className="flex gap-2">
                {[1, 2, 3, 4, 5].map((star) => (
                    <button
                        key={star}
                        type="button"
                        onClick={() => onChange(star)}
                        onMouseEnter={() => setHovered(star)}
                        onMouseLeave={() => setHovered(0)}
                        className="p-1 transition-transform hover:scale-110 active:scale-95"
                        data-testid={`star-${star}`}
                    >
                        <Star 
                            className={`h-10 w-10 sm:h-12 sm:w-12 transition-colors ${
                                star <= (hovered || value) 
                                    ? 'fill-amber-400 text-amber-400' 
                                    : 'text-muted-foreground/30'
                            }`} 
                        />
                    </button>
                ))}
            </div>
            <p className={`text-lg font-semibold ${
                value <= 2 ? 'text-red-500' : 
                value === 3 ? 'text-amber-500' : 
                'text-emerald-500'
            }`}>
                {labels[value] || 'Select a rating'}
            </p>
        </div>
    );
};

// Context Bubble Component - Uber style
const ContextBubble = ({ label, selected, onClick }) => (
    <button
        type="button"
        onClick={onClick}
        className={`px-4 py-2.5 rounded-full text-sm font-medium transition-all active:scale-95 border-2 ${
            selected 
                ? 'bg-primary text-primary-foreground border-primary' 
                : 'bg-card text-foreground border-border hover:border-primary/50 hover:bg-accent'
        }`}
    >
        {label}
    </button>
);

// Get context bubbles based on star rating
const getContextBubbles = (rating) => {
    const bubbles = {
        5: [
            { key: 'paid_on_time', label: 'Paid on time' },
            { key: 'easy_communication', label: 'Easy communication' },
            { key: 'clear_expectations', label: 'Clear job expectations' },
            { key: 'respectful', label: 'Respectful homeowner' },
            { key: 'would_work_again', label: 'Would work here again' },
            { key: 'professional', label: 'Professional' }
        ],
        4: [
            { key: 'minor_payment_delay', label: 'Minor payment delay' },
            { key: 'small_scope_change', label: 'Small scope change' },
            { key: 'good_overall', label: 'Overall good experience' },
            { key: 'smooth_job', label: 'Job went smoothly' },
            { key: 'good_communication', label: 'Good communication' },
            { key: 'would_recommend', label: 'Would recommend' }
        ],
        3: [
            { key: 'some_payment_delays', label: 'Some payment delays' },
            { key: 'scope_changed', label: 'Scope changed during job' },
            { key: 'communication_issues', label: 'Communication issues' },
            { key: 'took_longer', label: 'Job took longer than expected' },
            { key: 'scheduling_issues', label: 'Scheduling issues' },
            { key: 'minor_disputes', label: 'Minor disputes' }
        ],
        2: [
            { key: 'payment_delays', label: 'Payment delays' },
            { key: 'last_minute_changes', label: 'Last minute changes' },
            { key: 'poor_communication', label: 'Poor communication' },
            { key: 'disputed_scope', label: 'Disputed scope' },
            { key: 'difficult_client', label: 'Difficult client' },
            { key: 'unreasonable_demands', label: 'Unreasonable demands' }
        ],
        1: [
            { key: 'payment_problems', label: 'Payment problems' },
            { key: 'job_dispute', label: 'Job dispute' },
            { key: 'scope_changed_dramatically', label: 'Scope changed dramatically' },
            { key: 'hostile_behavior', label: 'Hostile behavior' },
            { key: 'cancellation_issues', label: 'Job cancellation issues' },
            { key: 'would_not_return', label: 'Would not return' }
        ]
    };
    return bubbles[rating] || bubbles[3];
};

const SubmitReview = () => {
    const { addressId } = useParams();
    const navigate = useNavigate();
    const { user, updateUser } = useAuth();
    const [address, setAddress] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [step, setStep] = useState(1);
    const [resendingVerification, setResendingVerification] = useState(false);
    const [reviewMode, setReviewMode] = useState('quick'); // 'quick' or 'detailed'
    
    // Beta Safety Layer state
    const [hasDuplicateReview, setHasDuplicateReview] = useState(false);
    const [isRateLimited, setIsRateLimited] = useState(false);
    const [rateLimitMessage, setRateLimitMessage] = useState('');
    const [showEvidenceWarning, setShowEvidenceWarning] = useState(false);
    
    // File upload refs and state
    const photoInputRef = useRef(null);
    const docInputRef = useRef(null);
    const [photoFile, setPhotoFile] = useState(null);
    const [docFile, setDocFile] = useState(null);
    const [uploadingEvidence, setUploadingEvidence] = useState(false);

    const [formData, setFormData] = useState({
        // Step 1: Job Details + Star Rating
        job_type: '',
        project_size: '',
        job_start_date: '',
        job_completion_date: '',
        contract_signed: false,
        payment_received: false,
        overall_rating: 3, // 1-5 star rating
        // Step 2: Context bubbles
        selected_bubbles: [],
        // Step 2: Detailed ratings (calculated from overall)
        ratings: {
            communication: 3,
            payment_timeliness: 3,
            schedule_reliability: 3,
            change_order_fairness: 3,
            jobsite_access: 3,
            professionalism: 3
        },
        // Step 2: Context flags
        context: {
            management_company_involved: false,
            accounting_payment_delay: false,
            insurance_claim_involved: false,
            permit_inspection_delay: false,
            weather_delay: false,
            scope_changed_mid_job: false,
            client_requested_pause: false,
            contractor_caused_delay: false,
            shared_fault: false,
            unresolved_punch_list: false
        },
        // Step 2: Outcome (derived from rating)
        would_work_again: true,
        require_deposit_next_time: false,
        decline_future_work: false,
        // Step 2: Notes
        notes: '',
        // Step 2: Verification
        job_confirmation: false
    });

    useEffect(() => {
        loadAddress();
        checkBetaSafetyLimits();
    }, [addressId]);

    const checkBetaSafetyLimits = async () => {
        try {
            // Check for duplicate review
            const dupCheck = await reviewAPI.checkDuplicate(addressId);
            if (dupCheck.data?.has_existing_review) {
                setHasDuplicateReview(true);
            }
            
            // Check rate limit
            const rateCheck = await reviewAPI.checkRateLimit();
            if (!rateCheck.data?.within_limit) {
                setIsRateLimited(true);
                setRateLimitMessage(rateCheck.data?.message || 'Rate limit exceeded');
            }
        } catch (err) {
            // Silent fail - checks will also be enforced server-side
            console.error('Beta safety check failed:', err);
        }
    };

    // Update derived fields when overall rating changes
    useEffect(() => {
        const rating = formData.overall_rating;
        // Set all detailed ratings to match overall
        const newRatings = {
            communication: rating,
            payment_timeliness: rating,
            schedule_reliability: rating,
            change_order_fairness: rating,
            jobsite_access: rating,
            professionalism: rating
        };
        // Set outcome based on rating
        const wouldWorkAgain = rating >= 3;
        const requireDeposit = rating <= 2;
        const declineFuture = rating <= 1;
        
        setFormData(prev => ({
            ...prev,
            ratings: newRatings,
            would_work_again: wouldWorkAgain,
            require_deposit_next_time: requireDeposit,
            decline_future_work: declineFuture
        }));
    }, [formData.overall_rating]);

    // Check if evidence warning should show (text > 150 chars, no evidence)
    useEffect(() => {
        const hasLongNotes = formData.notes && formData.notes.length > 150;
        const hasEvidence = photoFile || docFile;
        setShowEvidenceWarning(hasLongNotes && !hasEvidence);
    }, [formData.notes, photoFile, docFile]);

    const loadAddress = async () => {
        try {
            const response = await addressAPI.get(addressId);
            setAddress(response.data);
        } catch (err) {
            toast.error('Failed to load address');
            navigate('/dashboard');
        } finally {
            setLoading(false);
        }
    };

    const toggleBubble = (key) => {
        setFormData(prev => ({
            ...prev,
            selected_bubbles: prev.selected_bubbles.includes(key)
                ? prev.selected_bubbles.filter(k => k !== key)
                : [...prev.selected_bubbles, key]
        }));
    };

    const validateStep1 = () => {
        if (!formData.job_type) {
            toast.error('Please select a job type');
            return false;
        }
        if (!formData.project_size) {
            toast.error('Please select project size');
            return false;
        }
        return true;
    };

    const handleContinue = () => {
        if (validateStep1()) {
            setStep(2);
            window.scrollTo(0, 0);
        }
    };

    const handlePhotoSelect = (e) => {
        const file = e.target.files?.[0];
        if (file) {
            const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
            if (!validTypes.includes(file.type)) {
                toast.error('Invalid photo type. Allowed: JPG, PNG, WebP');
                return;
            }
            if (file.size > 5 * 1024 * 1024) {
                toast.error('Photo must be under 5MB');
                return;
            }
            setPhotoFile(file);
        }
    };

    const handleDocSelect = (e) => {
        const file = e.target.files?.[0];
        if (file) {
            const validTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
            if (!validTypes.includes(file.type)) {
                toast.error('Invalid document type. Allowed: PDF, JPG, PNG');
                return;
            }
            if (file.size > 10 * 1024 * 1024) {
                toast.error('Document must be under 10MB');
                return;
            }
            setDocFile(file);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.job_confirmation) {
            toast.error('You must confirm that you performed work at this address');
            return;
        }
        
        setSubmitting(true);

        try {
            // Build notes from bubbles if no custom notes
            let finalNotes = formData.notes;
            if (!finalNotes && formData.selected_bubbles.length > 0) {
                const bubbles = getContextBubbles(formData.overall_rating);
                const selectedLabels = formData.selected_bubbles
                    .map(key => bubbles.find(b => b.key === key)?.label)
                    .filter(Boolean);
                finalNotes = selectedLabels.join('. ');
            }

            const response = await reviewAPI.create({
                address_id: addressId,
                job_type: formData.job_type,
                project_size: formData.project_size,
                job_start_date: formData.job_start_date || new Date().toISOString().split('T')[0],
                job_completion_date: formData.job_completion_date || new Date().toISOString().split('T')[0],
                contract_signed: formData.contract_signed,
                payment_received: formData.payment_received,
                ratings: formData.ratings,
                would_work_again: formData.would_work_again,
                require_deposit_next_time: formData.require_deposit_next_time,
                decline_future_work: formData.decline_future_work,
                context: formData.context,
                notes: finalNotes,
                job_confirmation: formData.job_confirmation
            });
            
            const reviewId = response.data.id;
            
            // Upload evidence files if any
            if (photoFile || docFile) {
                setUploadingEvidence(true);
                const formDataUpload = new FormData();
                if (photoFile) formDataUpload.append('photo', photoFile);
                if (docFile) formDataUpload.append('document', docFile);
                
                try {
                    await reviewAPI.uploadEvidence(reviewId, formDataUpload);
                } catch (uploadErr) {
                    console.error('Evidence upload failed:', uploadErr);
                    toast.warning('Review submitted, but evidence upload failed.');
                }
            }
            
            navigate(`/review/success/${addressId}`);
        } catch (err) {
            const message = getErrorMessage(err, 'Failed to submit review');
            toast.error(message);
        } finally {
            setSubmitting(false);
            setUploadingEvidence(false);
        }
    };

    // Handle quick review submission
    const handleQuickSubmit = async (quickData) => {
        setSubmitting(true);
        try {
            await reviewAPI.create(quickData);
            navigate(`/review/success/${addressId}`);
        } catch (err) {
            const message = getErrorMessage(err, 'Failed to submit review');
            toast.error(message);
        } finally {
            setSubmitting(false);
        }
    };

    const handleResendVerification = async () => {
        if (!user?.email) return;
        setResendingVerification(true);
        try {
            await authAPI.resendVerification(user.email);
            toast.success('Verification email sent! Check your inbox.');
        } catch (err) {
            toast.error('Failed to send verification email. Please try again.');
        } finally {
            setResendingVerification(false);
        }
    };

    // BETA: Email verification bypassed during beta testing
    // TODO: Re-enable before public launch by uncommenting below
    /*
    // Show email verification required message
    if (!user?.email_verified) {
        return (
            <Layout>
                <div className="max-w-lg mx-auto px-4 py-12">
                    <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-6 text-center">
                        <div className="flex justify-center mb-4">
                            <div className="rounded-full bg-amber-500/20 p-4">
                                <Mail className="h-10 w-10 text-amber-600" />
                            </div>
                        </div>
                        <h2 className="text-xl font-bold text-foreground mb-2">Email Verification Required</h2>
                        <p className="text-muted-foreground mb-6">
                            You must verify your email address before you can submit reviews on ClientCheck. This helps us maintain the integrity of our contractor community.
                        </p>
                        <div className="space-y-3">
                            <Button 
                                onClick={handleResendVerification}
                                disabled={resendingVerification}
                                className="w-full"
                                data-testid="resend-verification-btn"
                            >
                                {resendingVerification ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Sending...
                                    </>
                                ) : (
                                    <>
                                        <Mail className="mr-2 h-4 w-4" />
                                        Resend Verification Email
                                    </>
                                )}
                            </Button>
                            <Button 
                                variant="outline"
                                onClick={() => navigate('/dashboard')}
                                className="w-full"
                            >
                                <ArrowLeft className="mr-2 h-4 w-4" />
                                Back to Dashboard
                            </Button>
                        </div>
                        <p className="text-xs text-muted-foreground mt-4">
                            Check your spam folder if you don't see the email. Verification links expire after 24 hours.
                        </p>
                    </div>
                </div>
            </Layout>
        );
    }
    */

    // Show duplicate review block
    if (hasDuplicateReview) {
        return (
            <Layout>
                <div className="max-w-lg mx-auto px-4 py-12">
                    <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-6 text-center">
                        <div className="flex justify-center mb-4">
                            <div className="rounded-full bg-red-500/20 p-4">
                                <AlertTriangle className="h-10 w-10 text-red-500" />
                            </div>
                        </div>
                        <h2 className="text-xl font-bold text-foreground mb-2">Review Already Submitted</h2>
                        <p className="text-muted-foreground mb-6">
                            You have already submitted a review for this property. Each contractor can only submit one review per property to ensure fair and accurate data.
                        </p>
                        <div className="space-y-3">
                            <Button 
                                onClick={() => navigate(`/property/${addressId}`)}
                                className="w-full"
                                data-testid="view-property-btn"
                            >
                                View Property Page
                                <ArrowRight className="ml-2 h-4 w-4" />
                            </Button>
                            <Button 
                                variant="outline"
                                onClick={() => navigate('/dashboard')}
                                className="w-full"
                            >
                                <ArrowLeft className="mr-2 h-4 w-4" />
                                Back to Dashboard
                            </Button>
                        </div>
                    </div>
                </div>
            </Layout>
        );
    }

    // Show rate limit block
    if (isRateLimited) {
        return (
            <Layout>
                <div className="max-w-lg mx-auto px-4 py-12">
                    <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-6 text-center">
                        <div className="flex justify-center mb-4">
                            <div className="rounded-full bg-amber-500/20 p-4">
                                <AlertTriangle className="h-10 w-10 text-amber-500" />
                            </div>
                        </div>
                        <h2 className="text-xl font-bold text-foreground mb-2">Slow Down</h2>
                        <p className="text-muted-foreground mb-6">
                            {rateLimitMessage || 'Too many reviews submitted. Please wait a few minutes before submitting another.'}
                        </p>
                        <Button 
                            variant="outline"
                            onClick={() => navigate('/dashboard')}
                            className="w-full"
                        >
                            <ArrowLeft className="mr-2 h-4 w-4" />
                            Back to Dashboard
                        </Button>
                    </div>
                </div>
            </Layout>
        );
    }

    if (loading) {
        return (
            <Layout>
                <div className="flex justify-center items-center min-h-[60vh]">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
            </Layout>
        );
    }

    const contextBubbles = getContextBubbles(formData.overall_rating);

    return (
        <Layout>
            <div className="max-w-lg mx-auto px-4 py-6">
                {/* Header */}
                <div className="flex items-center gap-3 mb-4">
                    <Button 
                        variant="ghost" 
                        size="icon" 
                        onClick={() => {
                            if (reviewMode === 'detailed' && step > 1) {
                                setStep(1);
                            } else if (reviewMode === 'detailed') {
                                setReviewMode('quick');
                            } else {
                                navigate(-1);
                            }
                        }}
                        className="h-10 w-10"
                        data-testid="back-btn"
                    >
                        <ArrowLeft className="h-5 w-5" />
                    </Button>
                    <div className="flex-1">
                        <h1 className="font-heading font-bold text-xl text-foreground">
                            Log Your Job
                        </h1>
                        <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                            <MapPin className="h-3.5 w-3.5" />
                            <span className="truncate">{address?.street_address}, {address?.city}</span>
                        </div>
                    </div>
                </div>

                {/* QUICK MODE - Default */}
                {reviewMode === 'quick' && (
                    <div className="space-y-6">
                        {/* Mode Toggle */}
                        <div className="flex gap-2 p-1 bg-accent rounded-lg">
                            <button
                                type="button"
                                onClick={() => setReviewMode('quick')}
                                className="flex-1 py-2.5 px-4 rounded-md font-medium text-sm transition-all bg-background text-foreground shadow-sm flex items-center justify-center gap-2"
                                data-testid="quick-mode-btn"
                            >
                                <Zap className="h-4 w-4" />
                                Quick (10 sec)
                            </button>
                            <button
                                type="button"
                                onClick={() => setReviewMode('detailed')}
                                className="flex-1 py-2.5 px-4 rounded-md font-medium text-sm transition-all text-muted-foreground hover:text-foreground flex items-center justify-center gap-2"
                                data-testid="detailed-mode-btn"
                            >
                                <ClipboardList className="h-4 w-4" />
                                Detailed
                            </button>
                        </div>

                        {/* Quick Review Form */}
                        <Card className="bg-card border-border">
                            <CardContent className="p-6">
                                <QuickReviewForm
                                    addressId={addressId}
                                    addressDisplay={`${address?.street_address}, ${address?.city}`}
                                    onSubmit={handleQuickSubmit}
                                    loading={submitting}
                                />
                            </CardContent>
                        </Card>
                    </div>
                )}

                {/* DETAILED MODE */}
                {reviewMode === 'detailed' && (
                    <>
                        {/* Mode Toggle */}
                        <div className="flex gap-2 p-1 bg-accent rounded-lg mb-6">
                            <button
                                type="button"
                                onClick={() => setReviewMode('quick')}
                                className="flex-1 py-2.5 px-4 rounded-md font-medium text-sm transition-all text-muted-foreground hover:text-foreground flex items-center justify-center gap-2"
                            >
                                <Zap className="h-4 w-4" />
                                Quick (10 sec)
                            </button>
                            <button
                                type="button"
                                onClick={() => setReviewMode('detailed')}
                                className="flex-1 py-2.5 px-4 rounded-md font-medium text-sm transition-all bg-background text-foreground shadow-sm flex items-center justify-center gap-2"
                            >
                                <ClipboardList className="h-4 w-4" />
                                Detailed
                            </button>
                        </div>

                        {/* Progress */}
                        <div className="flex items-center gap-2 mb-4">
                            <div className={`flex-1 h-1.5 rounded-full ${step >= 1 ? 'bg-primary' : 'bg-muted'}`} />
                            <div className={`flex-1 h-1.5 rounded-full ${step >= 2 ? 'bg-primary' : 'bg-muted'}`} />
                        </div>

                        {/* Moderation Reminder Banner - Updated for legal compliance */}
                        <div className="mb-6 p-3 bg-primary/5 border border-primary/20 rounded-lg" data-testid="review-disclaimer-banner">
                            <p className="text-xs text-muted-foreground text-center leading-relaxed">
                                Your ratings represent your personal perception and opinion of your job experience. 
                                Do not include factual claims, names, or identifying information about any individual.
                            </p>
                        </div>

                        <form onSubmit={handleSubmit}>
                            {step === 1 ? (
                                /* ========== STEP 1: Job Details + Star Rating ========== */
                                <div className="space-y-6">
                                    {/* Star Rating - Hero Section */}
                                    <div className="bg-card rounded-xl p-6 border border-border text-center">
                                        <h2 className="text-lg font-semibold text-foreground mb-4">How was your experience?</h2>
                                        <StarRating 
                                            value={formData.overall_rating}
                                            onChange={(val) => setFormData({...formData, overall_rating: val})}
                                        />
                                    </div>

                                    {/* Job Details */}
                                    <div className="bg-card rounded-xl p-4 border border-border space-y-4">
                                        <div className="flex items-center gap-2 text-foreground">
                                            <Briefcase className="h-4 w-4 text-primary" />
                                            <span className="font-medium text-sm">Job Details</span>
                                        </div>

                                        {/* Job Type */}
                                        <div>
                                    <Label className="text-xs text-muted-foreground mb-1.5 block">Job Type</Label>
                                    <Select
                                        value={formData.job_type}
                                        onValueChange={(val) => setFormData({...formData, job_type: val})}
                                    >
                                        <SelectTrigger className="bg-input border-border" data-testid="job-type-select">
                                            <SelectValue placeholder="Select job type" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {JOB_TYPES.map((type) => (
                                                <SelectItem key={type} value={type}>{type}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>

                                {/* Project Size */}
                                <div>
                                    <Label className="text-xs text-muted-foreground mb-1.5 block">Project Size</Label>
                                    <Select
                                        value={formData.project_size}
                                        onValueChange={(val) => setFormData({...formData, project_size: val})}
                                    >
                                        <SelectTrigger className="bg-input border-border" data-testid="project-size-select">
                                            <SelectValue placeholder="Select size" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="small">Small (Under $1K)</SelectItem>
                                            <SelectItem value="medium">Medium ($1K - $10K)</SelectItem>
                                            <SelectItem value="large">Large ($10K - $50K)</SelectItem>
                                            <SelectItem value="major">Major ($50K+)</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>

                                {/* Contract & Payment Toggles */}
                                <div className="grid grid-cols-2 gap-3 pt-2">
                                    <button
                                        type="button"
                                        onClick={() => setFormData({...formData, contract_signed: !formData.contract_signed})}
                                        className={`p-3 rounded-lg border-2 text-center transition-all ${
                                            formData.contract_signed 
                                                ? 'bg-primary/10 border-primary text-primary' 
                                                : 'bg-card border-border text-muted-foreground hover:border-primary/50'
                                        }`}
                                        data-testid="contract-toggle"
                                    >
                                        <FileText className="h-5 w-5 mx-auto mb-1" />
                                        <span className="text-xs font-medium">Contract</span>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setFormData({...formData, payment_received: !formData.payment_received})}
                                        className={`p-3 rounded-lg border-2 text-center transition-all ${
                                            formData.payment_received 
                                                ? 'bg-emerald-500/10 border-emerald-500 text-emerald-600 dark:text-emerald-400' 
                                                : 'bg-card border-border text-muted-foreground hover:border-emerald-500/50'
                                        }`}
                                        data-testid="paid-toggle"
                                    >
                                        <span className="text-lg mb-1 block">$</span>
                                        <span className="text-xs font-medium">Paid</span>
                                    </button>
                                </div>
                            </div>

                            {/* Continue Button */}
                            <Button
                                type="button"
                                onClick={handleContinue}
                                className="w-full h-12 text-base font-semibold"
                                data-testid="continue-btn"
                            >
                                Continue
                                <ArrowRight className="ml-2 h-4 w-4" />
                            </Button>
                        </div>
                    ) : (
                        /* ========== STEP 2: Context Bubbles + Notes ========== */
                        <div className="space-y-5">
                            {/* Context Bubbles */}
                            <div className="bg-card rounded-xl p-4 border border-border">
                                <h3 className="font-medium text-foreground text-sm mb-3">
                                    What best describes this experience?
                                </h3>
                                <p className="text-xs text-muted-foreground mb-4">
                                    Select all that apply
                                </p>
                                <div className="flex flex-wrap gap-2">
                                    {contextBubbles.map((bubble) => (
                                        <ContextBubble
                                            key={bubble.key}
                                            label={bubble.label}
                                            selected={formData.selected_bubbles.includes(bubble.key)}
                                            onClick={() => toggleBubble(bubble.key)}
                                        />
                                    ))}
                                </div>
                            </div>

                            {/* Notes (Optional) */}
                            <div className="bg-card rounded-xl p-4 border border-border">
                                <Label className="text-sm font-medium text-foreground mb-2 block">
                                    Additional Notes (Optional)
                                </Label>
                                <Textarea
                                    placeholder="Add any specific details..."
                                    value={formData.notes}
                                    onChange={(e) => setFormData({...formData, notes: e.target.value.slice(0, 250)})}
                                    className="min-h-[80px] bg-input border-border text-sm resize-none"
                                    maxLength={250}
                                    data-testid="notes-textarea"
                                />
                                <div className="flex justify-between mt-1.5 text-xs">
                                    <span className="text-muted-foreground">Facts only, no names</span>
                                    <span className={formData.notes.length > 200 ? 'text-amber-500' : 'text-muted-foreground'}>
                                        {formData.notes.length}/250
                                    </span>
                                </div>
                            </div>

                            {/* Evidence Warning Banner */}
                            {showEvidenceWarning && (
                                <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 flex items-start gap-2">
                                    <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
                                    <p className="text-sm text-amber-600 dark:text-amber-400">
                                        Consider uploading evidence to strengthen your review credibility.
                                    </p>
                                </div>
                            )}

                            {/* Evidence Upload */}
                            <div className="bg-card rounded-xl p-4 border border-border">
                                <div className="flex items-center gap-2 mb-3">
                                    <ShieldCheck className="h-4 w-4 text-primary" />
                                    <span className="font-medium text-foreground text-sm">Evidence (Optional)</span>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <input ref={photoInputRef} type="file" accept=".jpg,.jpeg,.png,.webp" onChange={handlePhotoSelect} className="hidden" />
                                    {photoFile ? (
                                        <div className="p-2 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                                            <div className="flex items-center justify-between">
                                                <Camera className="h-4 w-4 text-emerald-500" />
                                                <button type="button" onClick={() => setPhotoFile(null)} className="p-1">
                                                    <X className="h-3 w-3 text-emerald-500" />
                                                </button>
                                            </div>
                                            <p className="text-xs text-emerald-500 truncate mt-1">{photoFile.name}</p>
                                        </div>
                                    ) : (
                                        <button type="button" onClick={() => photoInputRef.current?.click()}
                                            className="p-3 border border-dashed border-border rounded-lg hover:border-primary/50 text-center">
                                            <Camera className="h-5 w-5 text-muted-foreground mx-auto mb-1" />
                                            <p className="text-xs text-muted-foreground">Photo</p>
                                        </button>
                                    )}

                                    <input ref={docInputRef} type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={handleDocSelect} className="hidden" />
                                    {docFile ? (
                                        <div className="p-2 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                                            <div className="flex items-center justify-between">
                                                <FileText className="h-4 w-4 text-emerald-500" />
                                                <button type="button" onClick={() => setDocFile(null)} className="p-1">
                                                    <X className="h-3 w-3 text-emerald-500" />
                                                </button>
                                            </div>
                                            <p className="text-xs text-emerald-500 truncate mt-1">{docFile.name}</p>
                                        </div>
                                    ) : (
                                        <button type="button" onClick={() => docInputRef.current?.click()}
                                            className="p-3 border border-dashed border-border rounded-lg hover:border-primary/50 text-center">
                                            <FileText className="h-5 w-5 text-muted-foreground mx-auto mb-1" />
                                            <p className="text-xs text-muted-foreground">Invoice</p>
                                        </button>
                                    )}
                                </div>
                                <p className="text-xs text-muted-foreground mt-2 text-center">Admin-only</p>
                            </div>

                            {/* Confirmation */}
                            <div className="bg-card border-2 border-primary/30 rounded-xl p-4">
                                <div className="flex items-start gap-3">
                                    <Checkbox
                                        id="job-confirmation"
                                        checked={formData.job_confirmation}
                                        onCheckedChange={(checked) => setFormData({...formData, job_confirmation: !!checked})}
                                        className="mt-0.5 border-primary data-[state=checked]:bg-primary"
                                        data-testid="job-confirmation-checkbox"
                                    />
                                    <label htmlFor="job-confirmation" className="text-sm text-foreground leading-snug cursor-pointer">
                                        <span className="font-medium">I confirm</span> that I personally performed work at this address.
                                    </label>
                                </div>
                                {!formData.job_confirmation && (
                                    <p className="text-xs text-red-500 mt-2 ml-6">* Required</p>
                                )}
                            </div>

                            {/* Submit Button */}
                            <Button
                                type="submit"
                                disabled={submitting || !formData.job_confirmation}
                                className="w-full h-12 text-base font-semibold disabled:opacity-50"
                                data-testid="submit-review-btn"
                            >
                                {submitting ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        {uploadingEvidence ? 'Uploading...' : 'Submitting...'}
                                    </>
                                ) : (
                                    <>
                                        <Send className="mr-2 h-4 w-4" />
                                        Submit Review
                                    </>
                                )}
                            </Button>
                        </div>
                    )}
                </form>
                    </>
                )}
            </div>
        </Layout>
    );
};

export default SubmitReview;
